import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useUserInfo } from '@/features/_data/useUserInfo';
import { IconLoading } from '@/icons';
import { Flex } from '@/ui/Flex/Flex';
import { useSelectedCustomerId } from '@/ui/Navbar/Navbar';

import { Shelf } from '@/types/Configuration';
import { useConfigurations } from '../_data/useConfigurations';
import { useUpdateConfigs } from '../_data/useUpdateConfigs';
import { CreatedPlayersModal } from './CreatedPlayersModal/CreatedPlayersModal';
import {
  SortBy,
  TableColumnsKeys,
} from './EditTable/ColumnHeading/ColumnHeading';
import { EditTable } from './EditTable/EditTable';
import { NoShelf } from './NoShelf/NoShelf';
import { TopBar } from './TopBar/TopBar';

const StyledLoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;

  svg {
    width: 48px;
    height: 48px;
    stroke: ${({ theme }) => theme.color('#676570')};
  }
`;

export type Config = {
  readonly playerTotals: {
    readonly online: number;
    readonly total: number;
  };
  readonly unitId: string;
  readonly id: string;
  readonly shelves: readonly Shelf[];
  readonly name: string;
  readonly companyName: string;
};

export type ConfigMap = readonly Config[] | null;

export type CreatedPlayersList = {
  readonly deviceType: string;
  readonly userId: number;
  readonly appUpdatePollInterval: number;
  readonly listenPort: number;
  readonly vpnPort: number;
  readonly apiEndpoint: string;
  readonly vpnEndpoint: string;
  readonly registryEndpoint: string;
  readonly deltaEndpoint: string;
  readonly mixpanelToken: string;
  readonly apiKey: string;
  readonly deviceId: number;
  readonly uuid: string;
  readonly deviceApiKey: string;
  readonly applicationId: number;
  readonly hostname?: string;
};

export const AdminEditPage = ({ isAdmin }: { readonly isAdmin: boolean }) => {
  const navigate = useNavigate();
  const { id: unitId } = useParams();
  const [selectedCustomerId] = useSelectedCustomerId();
  const { data: userInfo, isAdmin: admin } = useUserInfo();

  const { data, isLoading, refetch } = useConfigurations(
    isAdmin ? selectedCustomerId : userInfo?.customer,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [configState, setConfigState] = useState<ConfigMap>([]);

  const [sortBy, setSortBy] = useState<SortBy>({
    key: TableColumnsKeys.COMPANY,
    direction: 'DESC',
  });

  const [createdPlayersList, setCreatedPlayersList] = useState<
    readonly CreatedPlayersList[]
  >([]);

  const formattedData =
    data
      ?.filter((customer) => customer.id === unitId)
      .flatMap((customer) =>
        customer.subsidiaries.map((subsidiary) => {
          const playerTotals = subsidiary.shelves.reduce(
            (acc, shelf) => {
              if (shelf.archived) {
                return acc;
              }

              const onlinePlayers = shelf.players.filter(
                (player) => player.isOnline,
              ).length;

              const totalPlayers = shelf.players.length;

              return {
                online: acc.online + onlinePlayers,
                total: acc.total + totalPlayers,
              };
            },
            {
              online: 0,
              total: 0,
            },
          );

          return {
            companyName: customer.name,
            ...subsidiary,
            shelves: subsidiary.shelves
              .filter((shelf) => !shelf?.archived)
              .map((shelf) => ({
                ...shelf,
                players: shelf.players.filter((player) => !player.archived),
              })),
            playerTotals,
            unitId: customer.id,
          };
        }),
      ) ?? [];

  const { updateConfigs } = useUpdateConfigs(
    selectedCustomerId,
    formattedData,
    configState,
  );

  const handleSave = async () => {
    setLoading(true);
    const playerList = await updateConfigs();
    setCreatedPlayersList(playerList ?? []);
    await refetch();
    setLoading(false);
    if (playerList && playerList.length) {
      setIsModalOpen(true);
    }
  };

  // initialize form state
  useEffect(() => {
    setConfigState(formattedData);
  }, [data]);

  useEffect(() => {
    if (admin && !isAdmin) {
      navigate('/admin');
    }
  }, [admin, isAdmin]);

  if (isLoading) {
    return (
      <StyledLoadingWrapper>
        <IconLoading />
      </StyledLoadingWrapper>
    );
  }

  return (
    <>
      <CreatedPlayersModal
        createdPlayersList={createdPlayersList}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <Flex direction="column" grow padding={32}>
        {data && data?.length > 0 && configState ? (
          <>
            <TopBar
              configState={configState}
              data={formattedData}
              handleSave={handleSave}
              loading={loading}
              navigate={navigate}
            />

            <EditTable
              customer={data.find((customer) => customer.id === unitId)}
              configState={configState}
              setConfigState={setConfigState}
              setSortBy={setSortBy}
              sortBy={sortBy}
            />
          </>
        ) : (
          <NoShelf />
        )}
      </Flex>
    </>
  );
};
